import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { StressTestResults } from '../types/stressTest';
import type {
  PlanningCalculationUnit,
  PlanningRemedyResult,
  RemedyCandidate,
  PlanningBaseCaseAvailability,
  PlanningBaseCaseRequest,
  PlanningMeasureCatalog,
  PlanningResultVariant,
  PlanningRunListItem,
  PlanningRunResult,
} from '../types/planning';
import type { StressTestScenarioParameters } from '../types/scenario';
import type {
  PlanningJobStatus,
  PlanningRolloutDefaults,
  PlanningRolloutResult,
  PlanningRolloutStep,
} from '../types/planningRollout';
import { AnyNetworkElement } from '../types/network';
import { useScenarioStore } from './scenarioStore';
import { useZoneStore } from './zoneStore';
import { planningService } from '../services/planningService';
import { loadNetworkData } from '../services/networkDataService';

/**
 * Stressszenarien on archive basis. A run always yields two frames — the
 * observed (or labelled synthetic) base state and the same state with the
 * scenario changes — and the map/sidebars show whichever `resultVariant`
 * selects through the unchanged `stressTestResults` contract.
 */
interface StressTestState {
  /** Stress zone (isolated from the main map zone). */
  stressZoneId: string | null;
  /** Isolated network elements for the scenario map (loaded separately from the main map). */
  stressElements: AnyNetworkElement[];
  /** Active saved scenario id (DB) or null. */
  activeScenarioId: string | null;

  baseCase: PlanningBaseCaseRequest;
  /** §14a dimming active in the peak-load direction. */
  isControlActive: boolean;

  isCalculating: boolean;
  calculationError: string | null;

  /** The complete last run (persisted server-side). */
  run: PlanningRunResult | null;
  resultVariant: PlanningResultVariant;
  /** Frame currently shown (run.scenario or run.baseline) — the contract every map/sidebar consumer reads. */
  stressTestResults: StressTestResults | null;

  availability: PlanningBaseCaseAvailability | null;
  runHistory: PlanningRunListItem[];
  isHistoryLoading: boolean;
  /** Reinforcement catalogue (cable types, transformer ratings) for the measure editor. */
  catalog: PlanningMeasureCatalog | null;

  // ── Calculation unit (zone or Netzverbund) ──
  /** Unit under the scenario's coupling measures; member zones appear as bubbles, expanded ones with their elements. */
  unit: PlanningCalculationUnit | null;
  isLoadingUnit: boolean;
  /** Element id → zone id for elements of neighbour zones (the target zone's elements are absent). */
  elementZones: Record<string, string>;
  /** Neighbour zones whose elements are shown ("dazugeschaltet"); the target zone is always shown. */
  expandedZoneIds: string[];
  loadingZoneIds: string[];
  /** Loaded networks of neighbour zones (kept while the zone stays a member). */
  zoneElementCache: Record<string, AnyNetworkElement[]>;

  // ── "Abhilfe finden" ──
  remedies: PlanningRemedyResult | null;
  isFindingRemedies: boolean;
  remedyError: string | null;

  // ── Hochlaufpfad (rollout) ──
  /** 'scenario' = single what-if run, 'rollout' = Stützjahre × Monte Carlo. */
  planningMode: 'scenario' | 'rollout';
  rolloutDefaults: PlanningRolloutDefaults | null;
  rolloutPath: PlanningRolloutStep[];
  rolloutRuns: number;
  rolloutSeed: number;
  /** Anteil neuer PV mit iMSys + Steuerbox (0..1) — ohne iMSys greift die §9-EEG-60%-Kappung im Einspeisefall. */
  rolloutImsysShare: number;
  /** Grundlast-Drift in %/a (Default 0) — Haushaltsgrundverbrauch stagniert, Zuwachs kommt aus den Geräten. */
  rolloutBaseLoadDrift: number;
  /** Neubau: Element-Id eines hinzugefügten Hauses → erstes Stützjahr, in dem es existiert (fehlend = heute). */
  rolloutAddedYears: Record<string, number>;
  rolloutJob: PlanningJobStatus | null;
  rollout: PlanningRolloutResult | null;
  /** Selected Stützjahr (index into rollout.years; -1 = today) and direction shown on the map. */
  rolloutYearIndex: number;
  rolloutDirection: 'peakLoad' | 'feedIn' | 'summerPeak';
}

interface StressTestActions {
  setStressZoneId: (zoneId: string | null) => void;
  setStressElements: (elements: AnyNetworkElement[]) => void;
  getStressElement: (id: string) => AnyNetworkElement | undefined;
  selectScenario: (id: string) => void;
  applyScenarioParams: (params: StressTestScenarioParameters) => void;
  buildStressTestParams: () => StressTestScenarioParameters;
  setBaseCase: (baseCase: Partial<PlanningBaseCaseRequest>) => void;
  setControlActive: (active: boolean) => void;
  runStressTest: (zoneId?: string, controlBoxActivation?: boolean) => Promise<void>;
  activateControl: (zoneId?: string) => Promise<void>;
  deactivateControl: (zoneId?: string) => Promise<void>;
  setResultVariant: (variant: PlanningResultVariant) => void;
  loadAvailability: (zoneId: string) => Promise<void>;
  loadCatalog: () => Promise<void>;
  loadRunHistory: (zoneId: string) => Promise<void>;
  openRun: (runId: string) => Promise<void>;
  deleteRun: (runId: string) => Promise<void>;
  clearResults: () => void;
  reset: () => void;
  /**
   * Resolves the calculation unit for the zone's elements (already loaded by the page) and the
   * scenario's coupling measures, loads the neighbour zones and shows them on the map.
   */
  loadUnit: (zoneId: string, targetElements: AnyNetworkElement[]) => Promise<void>;
  /** Shows / hides a neighbour zone's elements on the planning map (the run always solves the whole unit). */
  toggleZone: (zoneId: string) => Promise<void>;
  /** Proposes and evaluates measures against the last run's limit breaches (same request, nothing persisted). */
  findRemedies: () => Promise<void>;
  /** Takes a candidate's measures into the scenario (replacing same-kind entries per element) and re-runs. */
  applyRemedy: (candidate: RemedyCandidate) => Promise<void>;

  setPlanningMode: (mode: 'scenario' | 'rollout') => void;
  loadRolloutDefaults: (zoneId: string) => Promise<void>;
  setRolloutPath: (path: PlanningRolloutStep[]) => void;
  setRolloutRuns: (runs: number) => void;
  setRolloutSeed: (seed: number) => void;
  setRolloutImsysShare: (share: number) => void;
  setRolloutBaseLoadDrift: (pctPerYear: number) => void;
  setRolloutAddedYear: (elementId: string, year: number | null) => void;
  /** Starts a rollout job; an explicit controlBoxActivation (banner toggle) overrides the sidebar checkbox and is written back to it. */
  startRollout: (controlBoxActivation?: boolean) => Promise<void>;
  selectRolloutYear: (index: number, direction?: 'peakLoad' | 'feedIn' | 'summerPeak') => void;
  openRolloutRun: (runId: string) => Promise<void>;
}

type StressTestStore = StressTestState & StressTestActions;

const initialBaseCase: PlanningBaseCaseRequest = { mode: 'archive', frame: 'peakLoad' };

const frameOf = (run: PlanningRunResult | null, variant: PlanningResultVariant): StressTestResults | null =>
  run ? (variant === 'baseline' ? run.baseline : run.scenario) : null;

/**
 * A scenario whose grid has no load-flow solution even without the change
 * (voltage collapse at share 0) has no scenario frame — the run opens on the
 * baseline and the "with changes" view stays unavailable.
 */
const hasScenarioFrame = (run: PlanningRunResult | null): boolean => !(run?.collapse && !run.collapse.hasLimitFrame);
const initialVariant = (run: PlanningRunResult): PlanningResultVariant => hasScenarioFrame(run) ? 'scenario' : 'baseline';

const rolloutFrameOf = (rollout: PlanningRolloutResult | null, yearIndex: number, direction: 'peakLoad' | 'feedIn' | 'summerPeak'): StressTestResults | null => {
  if (!rollout) return null;
  const dirs = yearIndex < 0 ? rollout.today : rollout.years[yearIndex]?.directions;
  return dirs?.find(d => d.direction === direction)?.representativeFrame ?? null;
};

let rolloutPollTimer: ReturnType<typeof setTimeout> | null = null;

/** Target zone elements + the expanded neighbour zones' elements, with an element → zone map for the neighbours. */
let targetElementsMemo: AnyNetworkElement[] = [];
function rebuildElements(targetElements?: AnyNetworkElement[]) {
  if (targetElements) targetElementsMemo = targetElements;
  const { expandedZoneIds, zoneElementCache } = useStressTestStore.getState();
  const known = new Set(targetElementsMemo.map(e => e.id));
  const elementZones: Record<string, string> = {};
  const extra: AnyNetworkElement[] = [];
  for (const z of expandedZoneIds) {
    for (const el of zoneElementCache[z] ?? []) {
      if (known.has(el.id)) continue;
      known.add(el.id);
      elementZones[el.id] = z;
      extra.push(el);
    }
  }
  useStressTestStore.setState({ stressElements: [...targetElementsMemo, ...extra], elementZones }, false, 'rebuildElements');
}

export const useStressTestStore = create<StressTestStore>()(
  devtools((set, get) => ({
    stressZoneId: sessionStorage.getItem('gridpilot_stress_zone_id'),
    stressElements: [],
    activeScenarioId: null,
    baseCase: initialBaseCase,
    isControlActive: false,
    isCalculating: false,
    calculationError: null,
    run: null,
    unit: null,
    isLoadingUnit: false,
    elementZones: {},
    expandedZoneIds: [],
    loadingZoneIds: [],
    zoneElementCache: {},
    remedies: null,
    isFindingRemedies: false,
    remedyError: null,
    resultVariant: 'scenario',
    stressTestResults: null,
    availability: null,
    runHistory: [],
    isHistoryLoading: false,
    catalog: null,
    planningMode: 'scenario',
    rolloutDefaults: null,
    rolloutPath: [],
    rolloutRuns: 20,
    rolloutSeed: 1,
    rolloutImsysShare: 0.7,
    rolloutBaseLoadDrift: 0,
    rolloutAddedYears: {},
    rolloutJob: null,
    rollout: null,
    rolloutYearIndex: 0,
    rolloutDirection: 'peakLoad',

    setStressZoneId: (zoneId) => {
      if (zoneId) {
        sessionStorage.setItem('gridpilot_stress_zone_id', zoneId);
      } else {
        sessionStorage.removeItem('gridpilot_stress_zone_id');
      }
      set({ stressZoneId: zoneId, stressElements: [], unit: null, elementZones: {}, expandedZoneIds: [], loadingZoneIds: [], zoneElementCache: {}, availability: null, runHistory: [], rolloutDefaults: null, rolloutPath: [], rollout: null, rolloutJob: null }, false, 'setStressZoneId');
    },

    setStressElements: (elements) => {
      set({ stressElements: elements }, false, 'setStressElements');
    },

    getStressElement: (id) => get().stressElements.find(e => e.id === id),

    selectScenario: (id) => {
      set({ activeScenarioId: id || null }, false, 'selectScenario');
    },

    applyScenarioParams: (params) => {
      set({
        baseCase: { ...initialBaseCase, ...(params.baseCase ?? {}) },
        isControlActive: params.controlBoxActivation ?? false,
      }, false, 'applyScenarioParams');
    },

    buildStressTestParams: () => ({
      type: 'stresstest' as const,
      baseCase: { ...get().baseCase },
      controlBoxActivation: get().isControlActive,
    }),

    setBaseCase: (baseCase) => {
      set(state => ({ baseCase: { ...state.baseCase, ...baseCase } }), false, 'setBaseCase');
    },

    setControlActive: (active) => {
      set({ isControlActive: active }, false, 'setControlActive');
    },

    runStressTest: async (zoneIdParam?: string, controlBoxActivation?: boolean) => {
      const zoneId = zoneIdParam ?? get().stressZoneId ?? useZoneStore.getState().activeZoneId;
      if (!zoneId) {
        set({ calculationError: 'Keine Zone ausgewählt' }, false, 'runStressTest/noZone');
        return;
      }

      const control = controlBoxActivation ?? get().isControlActive;
      set({ isCalculating: true, calculationError: null, isControlActive: control }, false, 'runStressTest/start');

      try {
        const { pendingOverrides, pendingAddedElements, pendingMeasures, currentScenario } = useScenarioStore.getState();
        const { baseCase, activeScenarioId } = get();
        const scenarioId = currentScenario?.id || activeScenarioId || undefined;

        const run = await planningService.run(zoneId, {
          scenarioId,
          elementOverrides: Object.keys(pendingOverrides).length > 0 ? pendingOverrides : undefined,
          addedElements: pendingAddedElements.length > 0 ? pendingAddedElements : undefined,
          measures: pendingMeasures.length > 0 ? pendingMeasures : undefined,
          baseCase: baseCase.frame === 'timestamp' ? baseCase : { mode: baseCase.mode, frame: baseCase.frame },
          controlBoxActivation: control || undefined,
        });

        set({
          run,
          remedies: null,
          remedyError: null,
          resultVariant: initialVariant(run),
          stressTestResults: frameOf(run, initialVariant(run)),
          isCalculating: false,
        }, false, 'runStressTest/success');

        void get().loadRunHistory(zoneId);
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Unbekannter Fehler';
        set({ calculationError: message, isCalculating: false }, false, 'runStressTest/error');
      }
    },

    // §14a toggle of the map banner: re-runs whatever the page currently shows —
    // the single scenario or the whole Hochlauf (all Stützjahre × runs).
    activateControl: async (zoneIdParam?: string) => {
      if (get().planningMode === 'rollout') {
        await get().startRollout(true);
        return;
      }
      await get().runStressTest(zoneIdParam, true);
    },

    deactivateControl: async (zoneIdParam?: string) => {
      if (get().planningMode === 'rollout') {
        await get().startRollout(false);
        return;
      }
      await get().runStressTest(zoneIdParam, false);
    },

    setResultVariant: (variant) => {
      set(state => {
        const shown = variant === 'scenario' && !hasScenarioFrame(state.run) ? 'baseline' : variant;
        return { resultVariant: shown, stressTestResults: frameOf(state.run, shown) };
      }, false, 'setResultVariant');
    },

    loadAvailability: async (zoneId) => {
      try {
        const availability = await planningService.getBaseCase(zoneId);
        if (get().stressZoneId === zoneId) {
          set({ availability }, false, 'loadAvailability');
        }
      } catch (error) {
        console.warn('[StressTestStore] Archivstatus nicht lesbar:', error);
        if (get().stressZoneId === zoneId) {
          set({ availability: null }, false, 'loadAvailability/error');
        }
      }
    },

    loadCatalog: async () => {
      const zoneId = get().stressZoneId;
      if (!zoneId || get().catalog) return;
      try {
        const catalog = await planningService.getCatalog(zoneId);
        set({ catalog }, false, 'loadCatalog');
      } catch (error) {
        console.warn('[StressTestStore] Maßnahmenkatalog nicht lesbar:', error);
      }
    },

    loadRunHistory: async (zoneId) => {
      set({ isHistoryLoading: true }, false, 'loadRunHistory/start');
      try {
        const runHistory = await planningService.listRuns(zoneId);
        if (get().stressZoneId === zoneId) {
          set({ runHistory, isHistoryLoading: false }, false, 'loadRunHistory');
        } else {
          set({ isHistoryLoading: false }, false, 'loadRunHistory/stale');
        }
      } catch (error) {
        console.warn('[StressTestStore] Planungsläufe nicht lesbar:', error);
        set({ isHistoryLoading: false }, false, 'loadRunHistory/error');
      }
    },

    openRun: async (runId) => {
      const zoneId = get().stressZoneId;
      if (!zoneId) return;
      set({ isCalculating: true, calculationError: null }, false, 'openRun/start');
      try {
        const run = await planningService.getRun(zoneId, runId);
        set({
          planningMode: 'scenario',
          run,
          resultVariant: initialVariant(run),
          stressTestResults: frameOf(run, initialVariant(run)),
          isControlActive: run.controlBoxActivation,
          isCalculating: false,
        }, false, 'openRun/success');
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Unbekannter Fehler';
        set({ calculationError: message, isCalculating: false }, false, 'openRun/error');
      }
    },

    deleteRun: async (runId) => {
      const zoneId = get().stressZoneId;
      if (!zoneId) return;
      try {
        await planningService.deleteRun(zoneId, runId);
        set(state => ({
          runHistory: state.runHistory.filter(r => r.id !== runId),
          ...(state.run?.runId === runId ? { run: null, stressTestResults: null } : {}),
        }), false, 'deleteRun');
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Unbekannter Fehler';
        set({ calculationError: message }, false, 'deleteRun/error');
      }
    },

    clearResults: () => {
      if (rolloutPollTimer) {
        clearTimeout(rolloutPollTimer);
        rolloutPollTimer = null;
      }
      set({ run: null, rollout: null, rolloutJob: null, stressTestResults: null, resultVariant: 'scenario', calculationError: null, isCalculating: false, remedies: null, remedyError: null, isFindingRemedies: false }, false, 'clearResults');
    },

    // ── Hochlaufpfad ──

    loadUnit: async (zoneId, targetElements) => {
      const couplingMeasures = useScenarioStore.getState().pendingMeasures.filter(m => m.kind === 'coupling_switch');
      set({ isLoadingUnit: true }, false, 'loadUnit/start');
      try {
        const unit = await planningService.getUnit(zoneId, couplingMeasures);
        if (get().stressZoneId !== zoneId) return;
        // Zones that left the unit (a Trennstelle was opened) disappear from the map.
        const expanded = get().expandedZoneIds.filter(z => unit.zoneIds.includes(z) && z !== zoneId);
        set({ unit, expandedZoneIds: expanded, isLoadingUnit: false }, false, 'loadUnit/success');
        rebuildElements(targetElements);
      } catch (error) {
        console.error('[StressTest] Recheneinheit nicht ladbar:', error);
        set({ unit: null, elementZones: {}, expandedZoneIds: [], stressElements: targetElements, isLoadingUnit: false }, false, 'loadUnit/error');
      }
    },

    toggleZone: async (zoneId) => {
      const { stressZoneId, expandedZoneIds, zoneElementCache, loadingZoneIds } = get();
      if (!stressZoneId || zoneId === stressZoneId || loadingZoneIds.includes(zoneId)) return;
      if (expandedZoneIds.includes(zoneId)) {
        set({ expandedZoneIds: expandedZoneIds.filter(z => z !== zoneId) }, false, 'toggleZone/collapse');
        rebuildElements();
        return;
      }
      if (!zoneElementCache[zoneId]) {
        set({ loadingZoneIds: [...loadingZoneIds, zoneId] }, false, 'toggleZone/load');
        try {
          const data = await loadNetworkData(zoneId);
          if (get().stressZoneId !== stressZoneId) return;
          set(s => ({ zoneElementCache: { ...s.zoneElementCache, [zoneId]: data.elements } }), false, 'toggleZone/loaded');
        } catch (error) {
          console.error(`[StressTest] Zone ${zoneId} nicht ladbar:`, error);
          set(s => ({ loadingZoneIds: s.loadingZoneIds.filter(z => z !== zoneId) }), false, 'toggleZone/error');
          return;
        }
        set(s => ({ loadingZoneIds: s.loadingZoneIds.filter(z => z !== zoneId) }), false, 'toggleZone/done');
      }
      set(s => ({ expandedZoneIds: s.expandedZoneIds.includes(zoneId) ? s.expandedZoneIds : [...s.expandedZoneIds, zoneId] }), false, 'toggleZone/expand');
      rebuildElements();
    },

    findRemedies: async () => {
      const zoneId = get().stressZoneId;
      const run = get().run;
      if (!zoneId || !run) return;
      set({ isFindingRemedies: true, remedyError: null }, false, 'findRemedies/start');
      try {
        const { pendingOverrides, pendingAddedElements, pendingMeasures, currentScenario } = useScenarioStore.getState();
        const { baseCase, activeScenarioId } = get();
        const remedies = await planningService.findRemedies(zoneId, {
          scenarioId: currentScenario?.id || activeScenarioId || undefined,
          elementOverrides: Object.keys(pendingOverrides).length > 0 ? pendingOverrides : undefined,
          addedElements: pendingAddedElements.length > 0 ? pendingAddedElements : undefined,
          measures: pendingMeasures.length > 0 ? pendingMeasures : undefined,
          baseCase: baseCase.frame === 'timestamp' ? baseCase : { mode: baseCase.mode, frame: baseCase.frame },
          controlBoxActivation: get().isControlActive || undefined,
        });
        if (get().stressZoneId !== zoneId) return;
        set({ remedies, isFindingRemedies: false }, false, 'findRemedies/success');
      } catch (error) {
        set({ remedyError: error instanceof Error ? error.message : 'Abhilfe-Suche fehlgeschlagen', isFindingRemedies: false }, false, 'findRemedies/error');
      }
    },

    applyRemedy: async (candidate) => {
      const { setMeasure } = useScenarioStore.getState();
      for (const m of candidate.measures) {
        setMeasure({ ...m, id: m.id || crypto.randomUUID() });
      }
      set({ remedies: null }, false, 'applyRemedy');
      await get().runStressTest();
    },

    setPlanningMode: (mode) => {
      set({
        planningMode: mode,
        stressTestResults: mode === 'rollout'
          ? rolloutFrameOf(get().rollout, get().rolloutYearIndex, get().rolloutDirection)
          : frameOf(get().run, get().resultVariant),
      }, false, 'setPlanningMode');
    },

    loadRolloutDefaults: async (zoneId) => {
      try {
        const defaults = await planningService.getRolloutDefaults(zoneId);
        if (get().stressZoneId !== zoneId) return;
        set(state => ({
          rolloutDefaults: defaults,
          rolloutPath: state.rolloutPath.length > 0 ? state.rolloutPath : defaults.path,
          rolloutRuns: defaults.monteCarloRuns,
        }), false, 'loadRolloutDefaults');
      } catch (error) {
        console.warn('[StressTestStore] Hochlauf-Vorgaben nicht lesbar:', error);
      }
    },

    setRolloutPath: (path) => set({ rolloutPath: path }, false, 'setRolloutPath'),
    setRolloutRuns: (runs) => set({ rolloutRuns: Math.max(1, Math.min(runs, get().rolloutDefaults?.maxMonteCarloRuns ?? 50)) }, false, 'setRolloutRuns'),
    setRolloutSeed: (seed) => set({ rolloutSeed: seed }, false, 'setRolloutSeed'),
    setRolloutImsysShare: (share) => set({ rolloutImsysShare: Math.max(0, Math.min(1, share)) }, false, 'setRolloutImsysShare'),
    setRolloutBaseLoadDrift: (pctPerYear) => set({ rolloutBaseLoadDrift: Math.max(-5, Math.min(5, pctPerYear)) }, false, 'setRolloutBaseLoadDrift'),
    setRolloutAddedYear: (elementId, year) => set((state) => {
      const next = { ...state.rolloutAddedYears };
      if (year == null) {
        delete next[elementId];
      } else {
        next[elementId] = year;
      }
      return { rolloutAddedYears: next };
    }, false, 'setRolloutAddedYear'),

    startRollout: async (controlBoxActivation?: boolean) => {
      const zoneId = get().stressZoneId;
      if (!zoneId) {
        set({ calculationError: 'Keine Zone ausgewählt' }, false, 'startRollout/noZone');
        return;
      }
      const { rolloutPath, rolloutRuns, rolloutSeed, rolloutImsysShare, rolloutBaseLoadDrift, rolloutAddedYears, baseCase, activeScenarioId } = get();
      const isControlActive = controlBoxActivation ?? get().isControlActive;
      if (rolloutPath.length === 0) {
        set({ calculationError: 'Der Hochlaufpfad braucht mindestens ein Stützjahr.' }, false, 'startRollout/noPath');
        return;
      }
      const { pendingOverrides, pendingAddedElements, pendingMeasures, currentScenario } = useScenarioStore.getState();
      // The previous result stays on the map while the job runs (like a scenario
      // re-run); the sidebar shows the progress card and the banner's §14a button
      // is disabled until the new result replaces it.
      set({ isCalculating: true, calculationError: null, rolloutJob: null, isControlActive }, false, 'startRollout/start');
      try {
        const job = await planningService.startRollout(zoneId, {
          scenarioId: currentScenario?.id || activeScenarioId || undefined,
          elementOverrides: Object.keys(pendingOverrides).length > 0 ? pendingOverrides : undefined,
          addedElements: pendingAddedElements.length > 0 ? pendingAddedElements : undefined,
          measures: pendingMeasures.length > 0 ? pendingMeasures : undefined,
          path: rolloutPath,
          monteCarloRuns: rolloutRuns,
          seed: rolloutSeed,
          newPvImsysShare: rolloutImsysShare,
          baseLoadDriftPctPerYear: rolloutBaseLoadDrift !== 0 ? rolloutBaseLoadDrift : undefined,
          addedElementYears: (() => {
            const entries = pendingAddedElements
              .filter(e => rolloutAddedYears[e.id] != null)
              .map(e => [e.id, rolloutAddedYears[e.id]] as const);
            return entries.length > 0 ? Object.fromEntries(entries) : undefined;
          })(),
          baseCaseMode: baseCase.mode,
          controlBoxActivation: isControlActive || undefined,
        });
        set({ rolloutJob: job }, false, 'startRollout/queued');

        const poll = async () => {
          if (get().stressZoneId !== zoneId) return;
          try {
            const status = await planningService.getRolloutJob(zoneId, job.jobId);
            if (status.state === 'done' && status.result) {
              const yearIndex = status.result.years.length - 1;
              set({
                rolloutJob: status,
                rollout: status.result,
                rolloutYearIndex: yearIndex,
                rolloutDirection: 'peakLoad',
                stressTestResults: rolloutFrameOf(status.result, yearIndex, 'peakLoad'),
                isCalculating: false,
              }, false, 'startRollout/done');
              void get().loadRunHistory(zoneId);
              return;
            }
            if (status.state === 'failed') {
              set({ rolloutJob: status, calculationError: status.error ?? 'Hochlauf fehlgeschlagen', isCalculating: false }, false, 'startRollout/failed');
              return;
            }
            set({ rolloutJob: status }, false, 'startRollout/progress');
            rolloutPollTimer = setTimeout(poll, 1500);
          } catch (error) {
            const message = error instanceof Error ? error.message : 'Unbekannter Fehler';
            set({ calculationError: message, isCalculating: false }, false, 'startRollout/pollError');
          }
        };
        rolloutPollTimer = setTimeout(poll, 1000);
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Unbekannter Fehler';
        set({ calculationError: message, isCalculating: false }, false, 'startRollout/error');
      }
    },

    selectRolloutYear: (index, direction) => {
      const dir = direction ?? get().rolloutDirection;
      set(state => ({
        rolloutYearIndex: index,
        rolloutDirection: dir,
        stressTestResults: rolloutFrameOf(state.rollout, index, dir),
      }), false, 'selectRolloutYear');
    },

    openRolloutRun: async (runId) => {
      const zoneId = get().stressZoneId;
      if (!zoneId) return;
      set({ isCalculating: true, calculationError: null }, false, 'openRolloutRun/start');
      try {
        const rollout = await planningService.getRolloutRun(zoneId, runId);
        const yearIndex = rollout.years.length - 1;
        set({
          planningMode: 'rollout',
          rollout,
          rolloutJob: null,
          rolloutYearIndex: yearIndex,
          rolloutDirection: 'peakLoad',
          rolloutPath: rollout.years.map(y => y.target),
          rolloutRuns: rollout.monteCarloRuns,
          rolloutSeed: rollout.seed,
          isControlActive: rollout.controlBoxActivation,
          stressTestResults: rolloutFrameOf(rollout, yearIndex, 'peakLoad'),
          isCalculating: false,
        }, false, 'openRolloutRun/success');
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Unbekannter Fehler';
        set({ calculationError: message, isCalculating: false }, false, 'openRolloutRun/error');
      }
    },

    reset: () => {
      set({
        activeScenarioId: null,
        baseCase: initialBaseCase,
        isControlActive: false,
        isCalculating: false,
        calculationError: null,
        run: null,
        resultVariant: 'scenario',
        stressTestResults: null,
        stressElements: [],
        availability: null,
        runHistory: [],
        isHistoryLoading: false,
        planningMode: 'scenario',
        rolloutDefaults: null,
        rolloutPath: [],
        rolloutJob: null,
        rollout: null,
        rolloutYearIndex: 0,
        rolloutDirection: 'peakLoad',
      }, false, 'reset');
    },
  }))
);
